import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface ButtonProps {
  children: React.ReactNode;
  to?: string;
  href?: string;
  variant?: 'primary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  className?: string;
}

const ButtonStyle = `
  display: inline-block;
  font-family: var(--font-primary);
  font-weight: var(--font-weight-light);
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
`;

const StyledButton = styled.button<{
  $variant?: 'primary' | 'outline';
  $size?: 'small' | 'medium' | 'large';
}>`
  ${ButtonStyle}
  background-color: ${(props) => (props.$variant === 'outline' ? 'transparent' : 'transparent')};
  color: ${(props) => (props.$variant === 'outline' ? 'var(--color-text)' : 'var(--color-text)')};
  border: 1px solid var(--color-text);
  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return 'var(--spacing-xs) var(--spacing-sm)';
      case 'large':
        return 'var(--spacing-sm) var(--spacing-lg)';
      default:
        return 'var(--spacing-sm) var(--spacing-md)';
    }
  }};
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '0.8rem';
      case 'large':
        return '1rem';
      default:
        return '0.9rem';
    }
  }};
  
  &:hover {
    background-color: transparent;
    color: var(--color-text);
    border-color: var(--color-text);
    opacity: 0.7;
  }
`;

const StyledLink = styled(Link)<{
  $variant?: 'primary' | 'outline';
  $size?: 'small' | 'medium' | 'large';
}>`
  ${ButtonStyle}
  background-color: ${(props) => (props.$variant === 'outline' ? 'transparent' : 'transparent')};
  color: ${(props) => (props.$variant === 'outline' ? 'var(--color-text)' : 'var(--color-text)')};
  border: 1px solid var(--color-text);
  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return 'var(--spacing-xs) var(--spacing-sm)';
      case 'large':
        return 'var(--spacing-sm) var(--spacing-lg)';
      default:
        return 'var(--spacing-sm) var(--spacing-md)';
    }
  }};
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '0.8rem';
      case 'large':
        return '1rem';
      default:
        return '0.9rem';
    }
  }};
  
  &:hover {
    background-color: transparent;
    color: var(--color-text);
    border-color: var(--color-text);
    opacity: 0.7;
  }
`;

const StyledExternalLink = styled.a<{
  $variant?: 'primary' | 'outline';
  $size?: 'small' | 'medium' | 'large';
}>`
  ${ButtonStyle}
  background-color: ${(props) => (props.$variant === 'outline' ? 'transparent' : 'transparent')};
  color: ${(props) => (props.$variant === 'outline' ? 'var(--color-text)' : 'var(--color-text)')};
  border: 1px solid var(--color-text);
  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return 'var(--spacing-xs) var(--spacing-sm)';
      case 'large':
        return 'var(--spacing-sm) var(--spacing-lg)';
      default:
        return 'var(--spacing-sm) var(--spacing-md)';
    }
  }};
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '0.8rem';
      case 'large':
        return '1rem';
      default:
        return '0.9rem';
    }
  }};
  
  &:hover {
    background-color: transparent;
    color: var(--color-text);
    border-color: var(--color-text);
    opacity: 0.7;
  }
`;

const Button: React.FC<ButtonProps> = ({
  children,
  to,
  href,
  variant = 'primary',
  size = 'medium',
  onClick,
  className,
}) => {
  if (to) {
    return (
      <StyledLink to={to} $variant={variant} $size={size} className={className}>
        {children}
      </StyledLink>
    );
  }

  if (href) {
    return (
      <StyledExternalLink 
        href={href} 
        $variant={variant} 
        $size={size} 
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </StyledExternalLink>
    );
  }

  return (
    <StyledButton $variant={variant} $size={size} onClick={onClick} className={className}>
      {children}
    </StyledButton>
  );
};

export default Button;
